/**
 * Read-only Markdown renderer for sermon notes (Grow cards + previews).
 *
 * Sermon-note bodies are authored in the WYSIWYG editor and stored as
 * Markdown. We render them with react-markdown + remark-gfm, sanitizing
 * the result (markdown permits raw links/HTML) per the codebase's
 * never-trust-input posture. Tailwind's preflight strips default list/
 * heading styling, so each element is restyled here to the Sanctuary
 * palette.
 *
 * Two features the editor offers have no native Markdown form, so the
 * serializer emits inline HTML for them: underline (`<u>`) and text
 * alignment (`style="text-align:…"` on paragraphs/headings). We parse
 * that HTML with rehype-raw and allow exactly those through the
 * sanitizer — nothing else beyond the default-safe set.
 *
 * Inline photos are stored as the app's own `/api/photos/<id>/large`
 * path — a relative URL the default sanitize schema allows, served by
 * the Worker with a family-scope re-check and an immutable cache.
 */
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

// Extend the default-safe schema with just the editor's two HTML-only
// features: the <u> tag and a text-align style on the blocks TextAlign
// targets. Everything else stays at react-markdown's hardened defaults.
const ALIGN_TAGS = ['p', 'h1', 'h2', 'h3'] as const;
const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  attributes: {
    ...defaultSchema.attributes,
    ...Object.fromEntries(
      ALIGN_TAGS.map((tag) => [
        tag,
        [...(defaultSchema.attributes?.[tag] ?? []), 'style'],
      ]),
    ),
  },
};

const components: Components = {
  h1: ({ children, style }) => (
    <h1 className="font-serif text-xl text-ink mt-3 mb-1" style={style}>
      {children}
    </h1>
  ),
  h2: ({ children, style }) => (
    <h2 className="font-serif text-lg text-ink mt-3 mb-1" style={style}>
      {children}
    </h2>
  ),
  h3: ({ children, style }) => (
    <h3 className="font-serif text-base text-ink mt-2 mb-1" style={style}>
      {children}
    </h3>
  ),
  p: ({ children, style }) => (
    <p className="text-ink leading-relaxed my-1.5" style={style}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-1.5 space-y-0.5 text-ink">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-1.5 space-y-0.5 text-ink">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  u: ({ children }) => <u className="underline">{children}</u>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-sage pl-3 my-2 text-soft italic font-serif">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-oxblood underline underline-offset-2"
    >
      {children}
    </a>
  ),
  img: ({ src, alt }) =>
    typeof src === 'string' ? (
      <img
        src={src}
        alt={alt ?? ''}
        loading="lazy"
        decoding="async"
        className="rounded-lg my-2 max-h-72 w-auto object-cover bg-line"
      />
    ) : null,
};

export function Markdown({ content }: { content: string }) {
  return (
    <div className="text-sm">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, schema]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
